import { useMemo, useRef, useState } from 'preact/hooks'
import type { EmailSendRuleDto } from '@/types/touchPoint'
import { EmailSendRuleKind, EmailOffsetUnit, EmailEventType } from '@/types/touchPoint'
import type { EmailRuleLookupResponse } from '@/types/lookups'

interface SentenceRuleEditorProps {
  value: EmailSendRuleDto
  onChange: (rule: EmailSendRuleDto) => void
  lookup: EmailRuleLookupResponse | null
  isLoading?: boolean
  /**
   * Hide the inline "Send this email on [date] at [time]" sentence for Scheduled rules.
   * When true, the parent owns the Scheduled timing UI (the tz-aware picker, ORB-352) and
   * this editor only renders the mode toggle + Event sentence.
   */
  hideScheduledTime?: boolean
  /** IANA zone seeded into ScheduledSendTimeZone when switching into Scheduled mode (ORB-352). */
  defaultTimeZone?: string
}

const UNIT_PLURAL: Record<EmailOffsetUnit, [string, string]> = {
  [EmailOffsetUnit.Minute]: ['minute', 'minutes'],
  [EmailOffsetUnit.Hour]: ['hour', 'hours'],
  [EmailOffsetUnit.Day]: ['day', 'days'],
}

export function SentenceRuleEditor({ value, onChange, lookup, isLoading, hideScheduledTime, defaultTimeZone }: SentenceRuleEditorProps) {
  const isScheduled = value.Kind === EmailSendRuleKind.Scheduled
  const isEvent = value.Kind === EmailSendRuleKind.Event

  const anchorOptions = useMemo(
    () => (lookup?.LegalEventTypes ?? []).map(a => ({ key: a.Key, label: a.Label, allowsNegative: a.AllowsNegativeOffset })),
    [lookup]
  )

  const activeAnchor = anchorOptions.find(a => a.key === value.EventType) ?? null
  const negativeAllowed = activeAnchor?.allowsNegative ?? false

  // Remember each mode's config so flipping "On a date" ⇄ "Anchored to an event" is non-lossy:
  // switching back restores the last anchor/offset (or date) instead of re-seeding defaults. The
  // emitted rule still only carries the active mode's fields.
  const eventMemory = useRef({
    EventType: value.Kind === EmailSendRuleKind.Event ? value.EventType ?? null : null,
    OffsetValue: value.Kind === EmailSendRuleKind.Event ? value.OffsetValue ?? 0 : 0,
    OffsetUnit: value.Kind === EmailSendRuleKind.Event ? value.OffsetUnit ?? EmailOffsetUnit.Day : EmailOffsetUnit.Day,
  })
  // Remember the Scheduled local wall-clock so flipping back into Scheduled restores it (ORB-352).
  const scheduledMemory = useRef<string | null>(
    value.Kind === EmailSendRuleKind.Scheduled ? value.ScheduledSendLocalTime ?? null : null
  )
  // Remember the chosen timezone too, so a round-trip Scheduled → Event → Scheduled doesn't lose it.
  const scheduledTimeZoneMemory = useRef<string | null>(
    value.Kind === EmailSendRuleKind.Scheduled ? value.ScheduledSendTimeZone ?? null : null
  )

  function setKind(next: EmailSendRuleKind) {
    if (next === value.Kind) return
    if (next === EmailSendRuleKind.Scheduled) {
      // Leaving Event — stash its config so we can restore it on the way back.
      eventMemory.current = {
        EventType: value.EventType ?? eventMemory.current.EventType,
        OffsetValue: value.OffsetValue ?? eventMemory.current.OffsetValue,
        OffsetUnit: value.OffsetUnit ?? eventMemory.current.OffsetUnit,
      }
      onChange({
        Kind: EmailSendRuleKind.Scheduled,
        // SendAtAbsolute is server-computed from the local-time/zone pair; never set it here.
        SendAtAbsolute: null,
        EventType: null,
        OffsetValue: null,
        OffsetUnit: null,
        ScheduledSendLocalTime: value.ScheduledSendLocalTime ?? scheduledMemory.current ?? defaultScheduledLocalTime(),
        // `|| null` (not `??`) so a blank remembered/default zone coalesces to null rather than "".
        ScheduledSendTimeZone:
          (value.ScheduledSendTimeZone ?? scheduledTimeZoneMemory.current ?? defaultTimeZone) || null,
      })
    } else {
      // Leaving Scheduled — stash its local time + timezone; restore the remembered event config.
      if (value.ScheduledSendLocalTime) scheduledMemory.current = value.ScheduledSendLocalTime
      if (value.ScheduledSendTimeZone) scheduledTimeZoneMemory.current = value.ScheduledSendTimeZone
      const remembered = eventMemory.current
      onChange({
        Kind: EmailSendRuleKind.Event,
        SendAtAbsolute: null,
        EventType: value.EventType ?? remembered.EventType ?? (anchorOptions[0]?.key as EmailEventType | undefined) ?? EmailEventType.WebRegistered,
        OffsetValue: value.OffsetValue ?? remembered.OffsetValue ?? 0,
        OffsetUnit: value.OffsetUnit ?? remembered.OffsetUnit ?? EmailOffsetUnit.Day,
        ScheduledSendLocalTime: null,
        ScheduledSendTimeZone: null,
      })
    }
  }

  return (
    <div className="space-y-3">
      <ModeToggle isScheduled={isScheduled} onChange={setKind} />

      {isScheduled && !hideScheduledTime && (
        <ScheduledSentence value={value} onChange={onChange} />
      )}

      {isEvent && (
        <EventSentence
          value={value}
          onChange={onChange}
          anchorOptions={anchorOptions}
          negativeAllowed={negativeAllowed}
          isLoading={isLoading}
        />
      )}
    </div>
  )
}

// ----- Mode toggle ---------------------------------------------------------

function ModeToggle({
  isScheduled,
  onChange,
}: {
  isScheduled: boolean
  onChange: (k: EmailSendRuleKind) => void
}) {
  return (
    <div className="inline-flex rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-0.5 text-xs">
      <ModeButton
        active={isScheduled}
        onClick={() => onChange(EmailSendRuleKind.Scheduled)}
      >
        On a date
      </ModeButton>
      <ModeButton
        active={!isScheduled}
        onClick={() => onChange(EmailSendRuleKind.Event)}
      >
        Anchored to an event
      </ModeButton>
    </div>
  )
}

function ModeButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: preact.ComponentChildren
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        'px-2.5 py-1 rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 ' +
        (active
          ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm font-medium'
          : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200')
      }
    >
      {children}
    </button>
  )
}

// ----- Scheduled sentence: "Send this email on [date] at [time]." ----------

function ScheduledSentence({
  value,
  onChange,
}: {
  value: EmailSendRuleDto
  onChange: (r: EmailSendRuleDto) => void
}) {
  const local = toLocalParts(value.SendAtAbsolute)

  return (
    <p className="text-xl leading-relaxed text-slate-900 dark:text-slate-100">
      Send this email on{' '}
      <DateToken
        value={local.date}
        onChange={d => onChange({ ...value, SendAtAbsolute: combineLocal(d, local.time) })}
      />
      {' '}at{' '}
      <TimeToken
        value={local.time}
        onChange={t => onChange({ ...value, SendAtAbsolute: combineLocal(local.date, t) })}
      />
      .
    </p>
  )
}

// ----- Event sentence: "Send [N] [unit] [before|after] [anchor]." ----------

function EventSentence({
  value,
  onChange,
  anchorOptions,
  negativeAllowed,
  isLoading,
}: {
  value: EmailSendRuleDto
  onChange: (r: EmailSendRuleDto) => void
  anchorOptions: { key: string; label: string; allowsNegative: boolean }[]
  negativeAllowed: boolean
  isLoading?: boolean
}) {
  const offsetAbs = Math.abs(value.OffsetValue ?? 0)
  const direction: 'before' | 'after' = (value.OffsetValue ?? 0) < 0 ? 'before' : 'after'
  const unit = value.OffsetUnit ?? EmailOffsetUnit.Day
  const isImmediate = offsetAbs === 0

  if (isImmediate) {
    return (
      <p className="text-xl leading-relaxed text-slate-900 dark:text-slate-100">
        Send this email on{' '}
        <AnchorToken
          value={value.EventType ?? null}
          options={anchorOptions}
          isLoading={isLoading}
          onChange={a => onChange({ ...value, EventType: a })}
        />
        .
        {' '}
        <button
          type="button"
          onClick={() =>
            onChange({
              ...value,
              OffsetValue: negativeAllowed ? -1 : 1,
              OffsetUnit: value.OffsetUnit ?? EmailOffsetUnit.Day,
            })
          }
          className="text-sm text-slate-500 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 underline decoration-dotted underline-offset-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 rounded"
        >
          + add offset
        </button>
      </p>
    )
  }

  return (
    <p className="text-xl leading-relaxed text-slate-900 dark:text-slate-100">
      Send this email{' '}
      <NumberToken
        value={offsetAbs}
        min={0}
        onChange={n => {
          const signed = direction === 'before' ? -Math.abs(n) : Math.abs(n)
          onChange({ ...value, OffsetValue: signed })
        }}
      />
      {' '}
      <UnitToken
        value={unit}
        plural={offsetAbs !== 1}
        onChange={u => onChange({ ...value, OffsetUnit: u })}
      />
      {' '}
      <DirectionToken
        value={direction}
        negativeAllowed={negativeAllowed}
        onChange={d => {
          const next = d === 'before' ? -Math.abs(value.OffsetValue ?? 0) : Math.abs(value.OffsetValue ?? 0)
          onChange({ ...value, OffsetValue: next })
        }}
      />
      {' '}
      <AnchorToken
        value={value.EventType ?? null}
        options={anchorOptions}
        isLoading={isLoading}
        onChange={a => onChange({ ...value, EventType: a })}
      />
      .
    </p>
  )
}

// ----- Tokens --------------------------------------------------------------

const TOKEN_BASE =
  'appearance-none bg-transparent border-0 border-b-2 border-dotted ' +
  'border-slate-300 dark:border-slate-600 px-1 -mx-0.5 ' +
  'text-slate-900 dark:text-slate-100 font-medium ' +
  'hover:border-primary-500 hover:bg-primary-50/60 dark:hover:bg-primary-900/20 ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 ' +
  'rounded-sm transition-colors cursor-pointer'

function NumberToken({
  value,
  min,
  onChange,
}: {
  value: number
  min: number
  onChange: (n: number) => void
}) {
  // Local draft so clearing the field mid-edit doesn't emit 0 — a 0 offset collapses the sentence to
  // "immediate" and unmounts this very input (the backspace-eats-the-offset bug). While the box is
  // empty we keep the committed value and just show blank; a real number commits up, and blur snaps
  // the display back to the committed value.
  const [draft, setDraft] = useState<string | null>(null)
  return (
    <input
      type="number"
      value={draft ?? String(value)}
      min={min}
      step={1}
      onInput={(e: Event) => {
        const raw = (e.target as HTMLInputElement).value
        setDraft(raw)
        if (raw === '') return
        const parsed = Number.parseInt(raw, 10)
        if (Number.isNaN(parsed)) return
        onChange(Math.max(min, parsed))
      }}
      onBlur={() => setDraft(null)}
      className={TOKEN_BASE + ' w-14 text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'}
      aria-label="Offset value"
    />
  )
}

function UnitToken({
  value,
  plural,
  onChange,
}: {
  value: EmailOffsetUnit
  plural: boolean
  onChange: (u: EmailOffsetUnit) => void
}) {
  const label = (u: EmailOffsetUnit) => (plural ? UNIT_PLURAL[u][1] : UNIT_PLURAL[u][0])
  return (
    <select
      value={value}
      onChange={(e: Event) => onChange((e.target as HTMLSelectElement).value as EmailOffsetUnit)}
      className={TOKEN_BASE}
      aria-label="Unit"
    >
      <option value={EmailOffsetUnit.Minute}>{label(EmailOffsetUnit.Minute)}</option>
      <option value={EmailOffsetUnit.Hour}>{label(EmailOffsetUnit.Hour)}</option>
      <option value={EmailOffsetUnit.Day}>{label(EmailOffsetUnit.Day)}</option>
    </select>
  )
}

function DirectionToken({
  value,
  negativeAllowed,
  onChange,
}: {
  value: 'before' | 'after'
  negativeAllowed: boolean
  onChange: (d: 'before' | 'after') => void
}) {
  return (
    <select
      value={value}
      onChange={(e: Event) => onChange((e.target as HTMLSelectElement).value as 'before' | 'after')}
      className={TOKEN_BASE}
      aria-label="Direction"
    >
      <option value="after">after</option>
      <option value="before" disabled={!negativeAllowed}>
        before
      </option>
    </select>
  )
}

function AnchorToken({
  value,
  options,
  isLoading,
  onChange,
}: {
  value: string | null
  options: { key: string; label: string }[]
  isLoading?: boolean
  onChange: (key: EmailEventType) => void
}) {
  if (isLoading) {
    return <span className="text-slate-400 dark:text-slate-500 italic">loading…</span>
  }
  if (options.length === 0) {
    return <span className="text-slate-400 dark:text-slate-500 italic">no anchors available</span>
  }
  return (
    <select
      value={value ?? ''}
      onChange={(e: Event) => onChange((e.target as HTMLSelectElement).value as EmailEventType)}
      className={TOKEN_BASE}
      aria-label="Anchor"
    >
      {!value && <option value="" disabled>pick an anchor</option>}
      {options.map(o => (
        <option key={o.key} value={o.key}>{o.label.toLowerCase()}</option>
      ))}
    </select>
  )
}

function DateToken({ value, onChange }: { value: string; onChange: (d: string) => void }) {
  return (
    <input
      type="date"
      value={value}
      onInput={(e: Event) => onChange((e.target as HTMLInputElement).value)}
      className={TOKEN_BASE + ' [color-scheme:light] dark:[color-scheme:dark]'}
      aria-label="Send date"
    />
  )
}

function TimeToken({ value, onChange }: { value: string; onChange: (t: string) => void }) {
  return (
    <input
      type="time"
      value={value}
      onInput={(e: Event) => onChange((e.target as HTMLInputElement).value)}
      className={TOKEN_BASE + ' [color-scheme:light] dark:[color-scheme:dark]'}
      aria-label="Send time"
    />
  )
}

// ----- Date helpers --------------------------------------------------------

// Format a Date's local wall time as YYYY-MM-DD. Using toISOString() here would
// shift the date by a day in zones west of UTC after noon-ish.
function localDateString(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function defaultScheduledTime(): string {
  // Tomorrow at 09:00 local wall time.
  const d = new Date()
  d.setDate(d.getDate() + 1)
  d.setHours(9, 0, 0, 0)
  return `${localDateString(d)}T09:00:00`
}

// Local wall-clock seed for a tz-aware Scheduled rule (ORB-352): tomorrow at 09:00, no offset.
function defaultScheduledLocalTime(): string {
  return defaultScheduledTime()
}

function toLocalParts(iso: string | null | undefined): { date: string; time: string } {
  if (!iso) {
    return { date: localDateString(new Date()), time: '09:00' }
  }
  // Strip trailing Z / timezone for display; treat as wall time.
  const trimmed = iso.replace(/Z$/, '').slice(0, 16)
  const [datePart, timePart] = trimmed.split('T')
  return { date: datePart ?? '', time: (timePart ?? '09:00').slice(0, 5) }
}

function combineLocal(date: string, time: string): string {
  return `${date}T${time}:00`
}
